import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Smartphone, Sparkles } from "lucide-react";
import SaleBanner from "@/components/SaleBanner";
import DemoLogins from "@/components/DemoLogins";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mocked } from "@/components/common";
import { LegalFooter } from "@/pages/PrivacyPolicy";

const HERO = "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&q=85";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthCallback() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/google", { session_id: sid })
      .then(async (r) => { await signIn(r.data); nav(r.data.user.role === "super_admin" ? "/admin" : "/dashboard", { replace: true }); })
      .catch((e) => { toast.error(errMsg(e)); nav("/login", { replace: true }); });
  }, []); // eslint-disable-line
  return <div className="min-h-screen grid place-items-center text-slate-500 font-mono text-sm" data-testid="auth-callback">Signing you in...</div>;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#F6F7F9] text-slate-100">
      <div className="hidden lg:flex relative overflow-hidden">
        <img src={HERO} alt="Architecture" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent" />
        <div className="relative mt-auto p-12 max-w-xl">
          <div className="lx-label mb-4">Real Estate Sales & Marketing OS</div>
          <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">Plan. Market. Launch.<br /><span className="text-cyan-400">Sell. Grow.</span></h1>
          <p className="text-slate-300 mt-4">Launch and sell your projects with AI. Plan campaigns, find buyers, and close bookings without depending on agencies.</p>
          <div className="flex gap-6 mt-8 font-mono text-xs text-slate-400"><span>10 AI ENGINES</span><span>MULTI-TENANT</span><span>FULL RBAC</span></div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 lx-grid-bg min-w-0">
        <div className="w-full max-w-md min-w-0 fade-up">
          <SaleBanner placement="home" />
          <img src="/logo.webp" alt="LaunchX360" className="h-16 w-auto mb-10" data-testid="auth-logo" />
          {children}
          <LegalFooter className="mt-10 !justify-start" />
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", phone: "", otp: "" });
  const [otpSent, setOtpSent] = useState(null);
  const [gBusy, setGBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const done = async (data) => { await signIn(data); nav(data.user.role === "super_admin" ? "/admin" : "/dashboard"); };

  return (
    <Shell>
      <h2 className="text-2xl font-bold">Welcome back</h2>
      <p className="text-sm text-slate-400 mt-1 mb-8">Sign in to your LaunchX360 workspace.</p>
      <Tabs defaultValue="email">
        <TabsList className="grid grid-cols-2 w-full mb-6">
          <TabsTrigger value="email" data-testid="login-tab-email"><Mail className="h-4 w-4 mr-2" />Email</TabsTrigger>
          <TabsTrigger value="otp" data-testid="login-tab-otp"><Smartphone className="h-4 w-4 mr-2" />Mobile OTP</TabsTrigger>
        </TabsList>
        <TabsContent value="email">
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); run(async () => done((await api.post("/auth/login", { email: f.email, password: f.password })).data)); }}>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="login-email-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" required value={f.password} onChange={set("password")} data-testid="login-password-input" /></div>
            <Button className="w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in..." : "Sign in"}</Button>
          </form>
        </TabsContent>
        <TabsContent value="otp">
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="flex items-center gap-2">Mobile number <Mocked what="SMS MOCKED" /></Label><Input placeholder="+919800000001" value={f.phone} onChange={set("phone")} data-testid="login-phone-input" /></div>
            {!otpSent ? (
              <Button className="w-full" disabled={busy || !f.phone} data-testid="login-send-otp-button"
                onClick={() => run(async () => { const r = await api.post("/auth/otp/send", { phone: f.phone }); setOtpSent(r.data.dev_otp); toast.success("OTP sent"); })}>Send OTP</Button>
            ) : (
              <>
                <div className="text-xs text-amber-400 font-mono" data-testid="login-dev-otp">Dev OTP (SMS gateway mocked): {otpSent}</div>
                <div className="space-y-1.5"><Label>Enter OTP</Label><Input maxLength={6} value={f.otp} onChange={set("otp")} data-testid="login-otp-input" /></div>
                <Button className="w-full" disabled={busy} data-testid="login-verify-otp-button"
                  onClick={() => run(async () => done((await api.post("/auth/otp/verify", { phone: f.phone, otp: f.otp })).data))}>Verify & Sign in</Button>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
      <Button variant="outline" disabled={gBusy} className="w-full mt-4 h-11 bg-white text-slate-100 border-slate-700 hover:bg-slate-900 hover:text-slate-100 focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-60" data-testid="login-google-button" onClick={() => { setGBusy(true); googleLogin(); }}>
        <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
        {gBusy ? "Redirecting to Google..." : "Continue with Google"}
      </Button>
      <p className="text-sm text-slate-400 mt-8">New to LaunchX360? <Link to="/register" className="text-emerald-400 hover:underline" data-testid="goto-register-link">Sign up with any email address</Link> <span className="text-xs">(Gmail not required)</span></p>
      <p className="text-sm text-slate-400 mt-2">Marketing agent? <Link to="/register?type=agent" className="text-emerald-400 hover:underline" data-testid="goto-agent-register-link">Register as a Marketing Agent</Link></p>
      <DemoLogins />
    </Shell>
  );
}

export function Register() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", organization_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const TYPES = [["developer", "Developer", "Company / Developer name"], ["marketing_agency", "Marketing Agency", "Agency name"], ["marketing_agent", "Marketing Agent", "Your business / brand name"],
    ["consultant", "Consultant", "Your practice / business name"], ["individual", "Individual", "Workspace name (e.g. your name)"]];
  const [type, setType] = useState(new URLSearchParams(window.location.search).get("type") === "agent" ? "marketing_agent" : "developer");
  const [offer, setOffer] = useState(null);
  useEffect(() => { api.get("/public/signup-offer").then((r) => setOffer(r.data)).catch(() => {}); }, []);
  const agent = type === "marketing_agent";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/register", { ...f, phone: f.phone || null, account_type: type });
      await signIn(r.data);
      toast.success(agent ? "Agent account created. Add your first developer company." : `Workspace created. ${offer?.credits ?? ""} AI credits added.`);
      nav(agent ? "/agent/companies" : "/dashboard");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const fields = [["organization_name", TYPES.find((t) => t[0] === type)[2], "text"], ["name", "Your full name", "text"], ["email", "Email (any provider, Gmail not required)", "email"], ["phone", "Mobile (for OTP login)", "tel"], ["password", "Password (min 8 chars)", "password"]];
  return (
    <Shell>
      {offer && <div className="lx-ai-badge mb-3" data-testid="register-offer-badge"><Sparkles className="h-3 w-3" />{offer.trial_days}-day free trial{offer.credits ? ` · ${offer.credits} AI credits` : ""}</div>}
      <h2 className="text-2xl font-bold mb-4">Create your workspace</h2>
      <Label className="text-xs text-slate-500">I am a</Label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1.5 mb-6" data-testid="register-account-type">
        {TYPES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setType(k)} data-testid={`register-type-${k}`}
            className={`text-sm py-2 px-2 rounded-md border transition-colors ${type === k ? "bg-emerald-600 border-emerald-600 text-white font-semibold" : "border-slate-800 text-slate-400 hover:bg-slate-800"}`}>{l}</button>))}
      </div>
      {agent && <p className="text-xs text-slate-500 -mt-3 mb-5" data-testid="register-agent-note">Marketing Agents get a flat 10% discount automatically on eligible LaunchX360 subscription and service payments (excludes taxes, Meta ad spend and third-party charges). You can also manage several companies from one login.</p>}
      <form className="space-y-4" onSubmit={submit}>
        {fields.map(([k, l, t]) => (
          <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type={t} required={k !== "phone"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`register-${k}-input`} /></div>
        ))}
        <Button className="w-full" disabled={busy} data-testid="register-submit-button">{busy ? "Creating..." : agent ? "Create agent account" : "Create workspace"}</Button>
      </form>
      <p className="text-sm text-slate-400 mt-6">Already have an account? <Link to="/login" className="text-emerald-400 hover:underline" data-testid="goto-login-link">Sign in</Link></p>
    </Shell>
  );
}
